import {resolvePremiumStatus, type User} from '@perfectpost/perfect-post-common';
import {useQueryClient} from '@tanstack/react-query';
import {ConsoleLogger} from 'aws-amplify/utils';
import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {sessionKeys} from './keys';
import {getPremiumStatus} from './premiumStatus';
import {useMeQuery} from './queries/useMeQuery';
import {usePremiumStatusQuery} from './queries/usePremiumStatusQuery';
import type {PremiumStatus} from '../../services/subscription';
import {usePreferences} from '../preferences/PreferencesProvider';

interface SessionValue {
  premiumStatus: PremiumStatus;
  /** Pause d'abonnement, incluant l'annonce optimiste faite par l'interface. */
  subscriptionPaused: boolean;
  subscriptionPausedUntil: string | undefined;
  /** Annonce immédiate d'un changement d'abonnement, avant que l'API ne suive. */
  setPremiumStatus: (status: PremiumStatus) => void;
  setSubscriptionPaused: (paused: {paused: boolean; pausedUntil?: string}) => void;
  /** Relit le profil et les faits premium : après un paiement Stripe, une résiliation. */
  refreshSession: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

const logger = new ConsoleLogger('SessionProvider');

/**
 * Statut premium de la session.
 *
 * POURQUOI UN CONTEXTE ET NON UN SIMPLE `select` DE REQUÊTE
 *
 * `getPremiumStatus` n'est pas une dérivation pure : le statut précédent
 * participe au calcul du suivant (cf. `premiumStatus.ts`). Il faut donc le
 * conserver quelque part — c'était `state.main.premiumStatus`, c'est maintenant
 * un état React.
 *
 * Deux vues l'écrivent aussi directement, sans passer par l'API : la bannière de
 * pause et la page d'annulation annoncent le nouveau statut dès que Stripe a
 * répondu, sans attendre que `GET /me` le reflète. C'étaient les actions
 * `setPremiumStatus` et `setSubscriptionPaused` ; ce sont les mêmes fonctions
 * ici.
 */
export function SessionProvider({children}: {children: React.ReactNode}) {
  const queryClient = useQueryClient();
  const {data: meData} = useMeQuery();
  const {data: premiumFacts, isError: premiumFactsUnavailable} = usePremiumStatusQuery();
  const user: User | undefined = meData?.me;

  const [premiumStatus, setPremiumStatusState] = useState<PremiumStatus>('free');
  const [pausedOverride, setPausedOverride] = useState<{paused: boolean; pausedUntil?: string} | undefined>(undefined);

  // Le statut courant est lu dans une `ref` et non dans les dépendances de
  // l'effet : il est une ENTRÉE du calcul, pas un déclencheur. L'y mettre
  // relancerait l'effet à chaque recalcul.
  const premiumStatusRef = useRef(premiumStatus);
  premiumStatusRef.current = premiumStatus;

  // Recalcul LOCAL, à chaque nouveau profil — c'étaient les reducers
  // `getMe.fulfilled` et `reloadPremium`. La licence d'équipe est le seul fait
  // que le profil ne porte pas : elle vient du dernier `GET /me/premium-status`.
  // Les groupes du jeton Cognito n'entrent plus dans le verdict.
  const hasTeamPremiumLicense = premiumFacts?.hasTeamPremiumLicense;
  useEffect(() => {
    setPremiumStatusState(getPremiumStatus({user, hasTeamPremiumLicense, existingStatus: premiumStatusRef.current}));
  }, [user, hasTeamPremiumLicense]);

  // Verdict SERVEUR, à chaque arrivée des faits — c'étaient les thunks
  // `authentification` et `refreshCognitoSession`. Déclaré APRÈS le recalcul
  // local : quand les faits arrivent avant le profil, c'est lui qui doit avoir
  // le dernier mot dans le même commit. Sans faits (appel en échec), le recalcul
  // local ci-dessus tient lieu de repli, comme le `catch` des thunks.
  useEffect(() => {
    if (premiumFacts === undefined) return;
    setPremiumStatusState(resolvePremiumStatus(premiumFacts).status);
  }, [premiumFacts]);

  useEffect(() => {
    if (premiumFactsUnavailable) {
      logger.warn('premium-status unavailable, falling back to local facts');
    }
  }, [premiumFactsUnavailable]);

  // Le mode d'apparence est choisi par les préférences LOCALES (le thème doit
  // s'appliquer avant toute requête), mais il est aussi enregistré sur le
  // serveur pour suivre l'utilisateur d'une machine à l'autre. On recopie donc
  // la valeur du serveur dans les préférences dès qu'elle arrive et qu'elle
  // diffère. C'était `redux-persist` qui tenait les deux bouts.
  const {appearanceMode, setAppearanceMode, setPostColumns} = usePreferences();
  useEffect(() => {
    const serverMode = user?.appearanceMode;
    if (serverMode !== undefined && serverMode !== appearanceMode) {
      setAppearanceMode(serverMode);
    }
  }, [user?.appearanceMode, appearanceMode, setAppearanceMode]);

  // Réconciliation INCONDITIONNELLE des colonnes du tableau de publications,
  // reprise du reducer `getMe.fulfilled`.
  //
  // Elle était auparavant conditionnée à la présence de préférences côté
  // serveur. Or elles ne sont écrites que si l'utilisateur a ouvert au moins une
  // fois le réglage des colonnes — ce que la plupart ne font jamais. Pour eux,
  // la liste restait celle relue du disque, figée au jour de leur première
  // visite : toute colonne AJOUTÉE ensuite y était absente, donc invisible pour
  // toujours.
  //
  // `setPostColumns` applique déjà l'ordre de précédence attendu — choix reçu,
  // puis choix local, puis valeur par défaut de la colonne — et aligne la liste
  // sur `DEFAULT_POST_COLUMNS`. Il suffit donc de lui passer ce que dit le
  // serveur, fût-ce rien.
  const serverPostColumns = user?.postColumns;
  useEffect(() => {
    if (user === undefined) return;
    setPostColumns(serverPostColumns ?? []);
  }, [user, serverPostColumns, setPostColumns]);

  const setSubscriptionPaused = useCallback((paused: {paused: boolean; pausedUntil?: string}) => {
    setPausedOverride(paused);
  }, []);

  const refreshSession = useCallback(async () => {
    // Plus de `fetchAuthSession({forceRefresh: true})` : il ne servait qu'à
    // rapatrier des groupes Cognito à jour, qui n'entrent plus dans le verdict
    // premium. Relire le profil et `GET /me/premium-status` suffit.
    await queryClient.invalidateQueries({queryKey: sessionKeys.all});
  }, [queryClient]);

  const value = useMemo(
    () => ({
      premiumStatus,
      subscriptionPaused: pausedOverride?.paused ?? user?.subscriptionPaused ?? false,
      subscriptionPausedUntil: pausedOverride?.pausedUntil ?? user?.subscriptionPausedUntil,
      setPremiumStatus: setPremiumStatusState,
      setSubscriptionPaused,
      refreshSession,
    }),
    [premiumStatus, pausedOverride, user, setSubscriptionPaused, refreshSession],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
